import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import SEO from '@/components/SEO';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils'; // Assuming this exists in shadcn setup
import { useLanguage } from '@/context/LanguageContext';

const NotFound = () => {
  const { t } = useLanguage();

  return (
    <main className="relative flex min-h-screen w-full flex-col items-center justify-center overflow-hidden bg-background p-4 text-center selection:bg-primary/20">
      <SEO
        title="404 - Oops, the System broke"
        description={t("404.description")}
        noindex={true}
      />

      {/* Main Content Container */}
      <div className="z-10 flex w-full max-w-4xl flex-col items-center gap-12">

        {/* H1 Heading */}
        <h1 className="text-4xl font-bold tracking-tight text-foreground sm:text-5xl md:text-6xl lg:text-7xl flex flex-col items-center gap-4">
          <span>404</span>
          <span className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl text-muted-foreground/80">
            Oops, the System broke
          </span>
        </h1>

        <div className="flex flex-col items-center gap-6">
          <h2 className="text-2xl font-medium text-foreground">
            Get professional help:
          </h2>

          {/* Primary CTA Button */}
          <Button
            asChild
            size="lg"
            className="relative h-14 min-w-[240px] rounded-full bg-primary px-8 text-lg font-medium text-primary-foreground shadow-lg transition-all duration-300 hover:scale-105 hover:bg-primary/90"
          >
            <a
              href="https://rassner-dev.org"
              target="_blank"
              rel="noopener noreferrer"
              className="group relative"
            >
              <div className="absolute -inset-1 rounded-full bg-primary/20 opacity-0 blur-lg transition duration-500 group-hover:opacity-75" />
              Visit rassner-dev.org
              <ArrowRight className="ml-2 h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
              <span className="sr-only">(opens in a new tab)</span>
            </a>
          </Button>
        </div>

        {/* Secondary Text */}
        <p className="text-lg font-medium text-muted-foreground/60">
          ...or try to fix it yourself:
        </p>

        {/* Interactive Puzzle Area */}
        <div className="relative h-[400px] w-full max-w-[800px]">
          <SystemPuzzle />
        </div>

      </div>
    </main>
  );
};

// --- Puzzle Implementation ---

interface PieceState {
  id: number;
  x: number;
  y: number;
  vx: number;
  vy: number;
  isDragging: boolean;
  rotation: number;
}

const SystemPuzzle = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const requestRef = useRef<number>();
  const mouseRef = useRef({ x: -1000, y: -1000 });
  const piecesRef = useRef<PieceState[]>([]);
  const [isMobile, setIsMobile] = useState(false);
  const [imagesLoaded, setImagesLoaded] = useState<boolean[]>(new Array(6).fill(false));

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Initialize pieces physics state
  useEffect(() => {
    piecesRef.current = Array.from({ length: 6 }).map((_, i) => ({
      id: i,
      x: (Math.random() - 0.5) * 40, // mild initial scatter
      y: (Math.random() - 0.5) * 40,
      vx: 0,
      vy: 0,
      isDragging: false,
      rotation: (Math.random() - 0.5) * 5
    }));
  }, []);

  const handleMouseMove = useCallback((e: React.MouseEvent | MouseEvent) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    mouseRef.current = {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    };
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent | TouchEvent) => {
    if (!containerRef.current || e.touches.length === 0) return;
    const rect = containerRef.current.getBoundingClientRect();
    mouseRef.current = {
      x: e.touches[0].clientX - rect.left,
      y: e.touches[0].clientY - rect.top
    };
  }, []);

  const handleMouseLeave = useCallback(() => {
    mouseRef.current = { x: -1000, y: -1000 };
  }, []);

  // Physics Loop
  useEffect(() => {
    let lastTime = performance.now();
    const DAMPING = 0.65; // Heavy friction - kills bounce
    const REPEL_DIST = 160;
    const REPEL_STRENGTH = 600;

    const animate = (time: number) => {
      lastTime = time;

      piecesRef.current.forEach((p, i) => {
        const el = document.getElementById(`puzzle-piece-${i}`);
        if (!el) return;

        if (!p.isDragging && !isMobile) {
          // Physics: Repel from mouse
          const row = Math.floor(i / 3);
          const col = i % 3;

          // These centers are approximate based on the overlapping layout
          const baseX = (col * 200) + 100; // 3 cols spanning 600px
          const baseY = (row * 175) + 87.5; // 2 rows spanning 350px

          const currentX = baseX + p.x;
          const currentY = baseY + p.y;

          const dx = currentX - mouseRef.current.x;
          const dy = currentY - mouseRef.current.y;
          const distSq = dx * dx + dy * dy;
          const dist = Math.sqrt(distSq);

          if (dist < REPEL_DIST) {
            const force = (1 - dist / REPEL_DIST) * REPEL_STRENGTH;
            const nx = dx / dist || 0.1;
            const ny = dy / dist || 0.1;
            p.vx += nx * force * 0.02;
            p.vy += ny * force * 0.02;
          }

          // Return to origin spring (weaker return)
          p.vx -= p.x * 0.01;
          p.vy -= p.y * 0.01;

          // Cap maximum velocity to ensure pieces are catchable
          const MAX_SPEED = 10;
          const speed = Math.sqrt(p.vx * p.vx + p.vy * p.vy);
          if (speed > MAX_SPEED) {
            p.vx = (p.vx / speed) * MAX_SPEED;
            p.vy = (p.vy / speed) * MAX_SPEED;
          }

          // Update
          p.x += p.vx;
          p.y += p.vy;
          p.vx *= DAMPING;
          p.vy *= DAMPING;
        }

        // Render
        el.style.transform = `translate3d(${p.x}px, ${p.y}px, 0) rotate(${p.rotation + (p.vx * 0.5)}deg)`;
      });

      requestRef.current = requestAnimationFrame(animate);
    };

    requestRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(requestRef.current!);
  }, [isMobile]);

  // Drag Handlers
  const handleDragStart = (e: React.MouseEvent | React.TouchEvent, id: number) => {
    const piece = piecesRef.current.find(p => p.id === id);
    if (piece) piece.isDragging = true;
  };

  const handleDragEnd = () => {
    piecesRef.current.forEach(p => p.isDragging = false);
  };

  useEffect(() => {
    window.addEventListener('mouseup', handleDragEnd);
    window.addEventListener('touchend', handleDragEnd);
    return () => {
      window.removeEventListener('mouseup', handleDragEnd);
      window.removeEventListener('touchend', handleDragEnd);
    };
  }, []);

  // Update drag position
  useEffect(() => {
    const dragLoop = () => {
      piecesRef.current.forEach((p, i) => {
        if (p.isDragging) {
          // Simple spring to mouse
          const row = Math.floor(i / 3);
          const col = i % 3;
          const baseX = (col * 200) + 100;
          const baseY = (row * 175) + 87.5;

          const targetX = mouseRef.current.x - baseX;
          const targetY = mouseRef.current.y - baseY;

          p.vx += (targetX - p.x) * 0.2;
          p.vy += (targetY - p.y) * 0.2;
          p.vx *= 0.7; // Heavy damping
          p.vy *= 0.7;
          p.x += p.vx;
          p.y += p.vy;
        }
      });
      requestAnimationFrame(dragLoop);
    };
    const id = requestAnimationFrame(dragLoop);
    return () => cancelAnimationFrame(id);
  }, []);

  // CLIP PATHS (Fallback Shapes)
  const fallbackClipPaths = [
    'polygon(0% 0%, 100% 0%, 100% 40%, 85% 50%, 100% 60%, 100% 100%, 0% 100%, 0% 60%, 15% 50%, 0% 40%)', // Generic 'Side' piece
    'polygon(0% 0%, 40% 0%, 50% 15%, 60% 0%, 100% 0%, 100% 100%, 60% 100%, 50% 85%, 40% 100%, 0% 100%)', // Top/Bot interlock
    'polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)', // Fallback rect
    'polygon(15% 0%, 100% 0%, 100% 100%, 0% 100%, 0% 60%, 15% 50%, 0% 40%)', // Left socket
    'polygon(0% 0%, 100% 0%, 85% 40%, 100% 50%, 85% 60%, 100% 100%, 0% 100%)', // Right socket
    'polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)'
  ];

  return (
    <div
      ref={containerRef}
      className="active:cursor-grabbing relative mx-auto h-[350px] w-full max-w-[600px] cursor-grab select-none touch-none"
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      onTouchMove={handleTouchMove}
    >
      {Array.from({ length: 6 }).map((_, i) => {
        const row = Math.floor(i / 3);
        const col = i % 3;

        return (
          <div
            key={i}
            id={`puzzle-piece-${i}`}
            onMouseDown={(e) => handleDragStart(e, i)}
            onTouchStart={(e) => handleDragStart(e, i)}
            className="absolute will-change-transform z-10 hover:z-20 transition-colors"
            style={{
              left: `${col * 30}%`,
              top: `${row * 45}%`,
              width: '40%',
              height: '60%',
            }}
          >
            {/* 1. Try to load CUSTOM IMAGE */}
            <img
              src={`/images/puzzle/${i + 1}.png`}
              alt={`Puzzle Piece ${i + 1}`}
              className={cn(
                "absolute inset-0 h-full w-full object-contain pointer-events-none transition-opacity duration-300 drop-shadow-xl hover:brightness-110",
                imagesLoaded[i] ? "opacity-100" : "opacity-0"
              )}
              onLoad={() => {
                setImagesLoaded(prev => {
                  const newLoaded = [...prev];
                  newLoaded[i] = true;
                  return newLoaded;
                });
              }}
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />

            {/* 2. FALLBACK: Styled Box with Text */}
            {!imagesLoaded[i] && (
              <div
                className="absolute inset-0 flex items-center justify-center overflow-hidden bg-background/80 border-2 border-primary/40 text-primary shadow-lg backdrop-blur-md"
                style={{
                  // Use a jagged clip path if fallback
                  clipPath: fallbackClipPaths[i % fallbackClipPaths.length],
                }}
              >
                <span
                  className="absolute font-black tracking-widest pointer-events-none select-none text-primary/20"
                  style={{
                    fontSize: 'clamp(2rem, 8vw, 4rem)',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {['SYS', 'TEM', 'ERR', '404', '???', '!!!'][i]}
                </span>
                {/* Inner glow/highlight */}
                <div className="absolute inset-0 bg-primary/10 pointer-events-none" />
              </div>
            )}

          </div>
        );
      })}
    </div>
  );
};

export default NotFound;
